/**
 * Azure OpenAI Realtime WebSocket Client
 * 
 * Manages the WebSocket connection to Azure OpenAI Realtime API,
 * handles the protocol translation between Azure's event-based format
 * and the application's event emitter interface.
 * 
 * Migrated from Google Gemini Live API.
 */

import { EventEmitter } from "eventemitter3";
import {
  LiveClientOptions,
  StreamingLog,
  Part,
  Content,
  ServerContent,
  ToolCall,
  ToolCallCancellation,
  ClientToolResponse,
  ServerMessage,
} from "../types";
import { base64ToArrayBuffer } from "./utils";
import { logger } from "../utils/logger";

/**
 * Event types that can be emitted by the GenAILiveClient.
 */
export interface LiveClientEventTypes {
  audio: (data: ArrayBuffer) => void;
  close: (event: CloseEvent) => void;
  content: (data: ServerContent) => void;
  error: (error: ErrorEvent) => void;
  interrupted: () => void;
  log: (log: StreamingLog) => void;
  open: () => void;
  setupcomplete: () => void;
  sessionReady: () => void;
  toolcall: (toolCall: ToolCall) => void;
  toolcallcancellation: (toolcallCancellation: ToolCallCancellation) => void;
  turncomplete: () => void;
  inputTranscription: (text: string) => void;
  outputTranscription: (text: string) => void;
}

/**
 * Azure OpenAI Realtime WebSocket client.
 * Replaces the Gemini GenAI SDK-based client with direct Azure WebSocket protocol.
 */
export class GenAILiveClient extends EventEmitter<LiveClientEventTypes> {
  private _ws: WebSocket | null = null;
  private _status: "connected" | "disconnected" | "connecting" = "disconnected";
  private _endpoint: string;
  private _apiKey: string;
  private _deployment: string;
  private _apiVersion: string;
  private _proxyMode: boolean = false;

  // Track accumulated audio deltas for current response
  private _currentResponseId: string | null = null;

  // Track function calls across events
  private _pendingFunctionCalls: Map<string, { id: string; name: string; args: string }> = new Map();

  public get status() {
    return this._status;
  }

  public get model() {
    return this._deployment;
  }

  // Session config stored after connect
  private _sessionConfig: Record<string, any> | null = null;

  public getConfig() {
    return { ...this._sessionConfig };
  }

  constructor(options: LiveClientOptions) {
    super();
    this._endpoint = options.endpoint.replace(/\/$/, "");
    this._apiKey = options.apiKey || "";
    this._deployment = options.deployment;
    this._apiVersion = options.apiVersion;

    console.log("🔑 [AZURE] Initializing Azure OpenAI Realtime client");
  }

  protected log(type: string, message: StreamingLog["message"]) {
    const log: StreamingLog = {
      date: new Date(),
      type,
      message,
    };
    this.emit("log", log);
  }

  /**
   * Connect to Azure OpenAI Realtime API via WebSocket
   * @param _model Unused (kept for interface compatibility)
   * @param sessionConfig The session configuration from backend
   */
  async connect(_model: string, sessionConfig: Record<string, any>): Promise<boolean> {
    if (this._status === "connected" || this._status === "connecting") {
      logger.connection("Connection already in progress or connected, skipping");
      return false;
    }

    logger.connection("Initiating connection to Azure OpenAI Realtime API");
    logger.connection("Deployment:", this._deployment);

    this._status = "connecting";
    this._sessionConfig = sessionConfig;

    // Build WebSocket URL for Azure OpenAI Realtime
    const wsUrl = `${this._endpoint.replace("https://", "wss://")}/openai/realtime?api-version=${this._apiVersion}&deployment=${this._deployment}`;

    return new Promise((resolve) => {
      try {
        // Azure Realtime uses api-key header — WebSocket API doesn't support custom headers
        // so we pass the key as a query parameter (standard Azure pattern)
        const urlWithAuth = this._apiKey ? `${wsUrl}&api-key=${this._apiKey}` : wsUrl;
        const ws = new WebSocket(urlWithAuth);
        this._ws = ws;

        ws.onopen = () => {
          console.log("✅ [AZURE] WebSocket connected to Azure OpenAI Realtime");
          this._status = "connected";

          // Send session.update with the config from backend
          if (sessionConfig && Object.keys(sessionConfig).length > 0) {
            const updateMsg = {
              type: "session.update",
              session: sessionConfig,
            };
            ws.send(JSON.stringify(updateMsg));
            console.log("📤 [AZURE] Sent session.update with agent configuration");
          }

          this.emit("open");
          resolve(true);
        };

        ws.onmessage = async (event: MessageEvent) => {
          try {
            let messageText: string;
            if (event.data instanceof Blob) {
              messageText = await event.data.text();
            } else {
              messageText = event.data;
            }

            const data = JSON.parse(messageText);
            this.handleAzureRealtimeEvent(data);
          } catch (e) {
            console.error("❌ [AZURE] Error parsing WebSocket message:", e);
          }
        };

        ws.onerror = (event: Event) => {
          console.error("❌ [AZURE] WebSocket error:", event);
          this._status = "disconnected";
          this.emit("error", event as ErrorEvent);
          resolve(false);
        };

        ws.onclose = (event: CloseEvent) => {
          console.log(`🔒 [AZURE] WebSocket closed. Code: ${event.code}, Reason: ${event.reason || "No reason"}`);
          this._status = "disconnected";
          this._ws = null;
          this.emit("close", event);
        };
      } catch (error) {
        console.error("❌ [AZURE] Failed to create WebSocket:", error);
        this._status = "disconnected";
        resolve(false);
      }
    });
  }

  /**
   * Connect via the backend WebSocket proxy (no API key sent to frontend).
   * The backend relays messages to Azure with the key server-side.
   *
   * @param backendUrl - Backend base URL (http://localhost:8000)
   * @param agentId - Agent to connect to
   * @param voiceGender - Voice gender preference
   */
  async connectViaProxy(
    backendUrl: string,
    agentId: string,
    voiceGender: string = "male",
  ): Promise<boolean> {
    if (this._status === "connected" || this._status === "connecting") {
      logger.connection("Connection already in progress or connected, skipping");
      return false;
    }

    logger.connection("Initiating connection via backend proxy");
    logger.connection("Agent:", agentId, "Voice:", voiceGender);

    this._status = "connecting";
    this._proxyMode = true;

    // Build WebSocket URL to our own backend proxy — NO API key in the URL
    const wsBase = backendUrl
      .replace("https://", "wss://")
      .replace("http://", "ws://")
      .replace(/\/$/, "");
    const wsUrl = `${wsBase}/ws/realtime/${agentId}?voice_gender=${voiceGender}`;

    return new Promise((resolve) => {
      try {
        const ws = new WebSocket(wsUrl);
        this._ws = ws;

        ws.onopen = () => {
          console.log("✅ [PROXY] Connected via backend proxy (API key hidden)");
          this._status = "connected";
          this.emit("open");
          // Do NOT resolve yet — wait for session.updated (sessionReady) so the backend's
          // system instruction is confirmed before we send the initial prompt.
        };

        // Resolve once the session is fully configured (session.updated received).
        // 10-second fallback: Azure Realtime can be slow to complete the handshake,
        // especially under load or on the first connection. 3 s was too short and
        // caused the fallback to fire before session.updated arrived, meaning agents
        // activated against an unconfigured session (initial prompts were ignored).
        const fallbackTimer = setTimeout(() => {
          console.warn("⚠️ [PROXY] sessionReady timeout — resolving anyway");
          this.off("sessionReady", onSessionReady);
          resolve(true);
        }, 10000);

        const cleanup = () => {
          clearTimeout(fallbackTimer);
          this.off("sessionReady", onSessionReady);
        };

        const onSessionReady = () => {
          cleanup();
          resolve(true);
        };
        this.on("sessionReady", onSessionReady);

        ws.onmessage = async (event: MessageEvent) => {
          try {
            let messageText: string;
            if (event.data instanceof Blob) {
              messageText = await event.data.text();
            } else {
              messageText = event.data;
            }
            const data = JSON.parse(messageText);
            this.handleAzureRealtimeEvent(data);
          } catch (e) {
            console.error("❌ [PROXY] Error parsing WebSocket message:", e);
          }
        };

        ws.onerror = (event: Event) => {
          console.error("❌ [PROXY] WebSocket error:", event);
          cleanup();
          this._status = "disconnected";
          this.emit("error", event as ErrorEvent);
          resolve(false);
        };

        ws.onclose = (event: CloseEvent) => {
          console.log(`🔒 [PROXY] WebSocket closed. Code: ${event.code}, Reason: ${event.reason || "No reason"}`);
          // If the socket closes before session.updated arrived (e.g. code 1011 handshake
          // timeout), resolve false immediately so the caller can retry rather than waiting
          // the full 10-second fallback.
          cleanup();
          this._status = "disconnected";
          this._ws = null;
          this.emit("close", event);
          resolve(false);
        };
      } catch (error) {
        console.error("❌ [PROXY] Failed to create WebSocket:", error);
        this._status = "disconnected";
        resolve(false);
      }
    });
  }

  /**
   * Handle incoming Azure OpenAI Realtime events and translate them
   * to the application's event format.
   */
  private handleAzureRealtimeEvent(event: any) {
    const eventType = event.type;

    switch (eventType) {
      // ---- Session events ----
      case "session.created":
        console.log(`📋 [AZURE] ${eventType}`);
        this.emit("setupcomplete");
        break;
      case "session.updated":
        console.log(`📋 [AZURE] ${eventType}`);
        this.emit("setupcomplete");
        // session.updated means the backend has applied the full system instruction.
        // Emit a separate event so connectViaProxy (cold path) can wait for it
        // before sending the initial prompt — prevents responding from an empty session.
        this.emit("sessionReady");
        break;

      // ---- Response lifecycle ----
      case "response.created":
        this._currentResponseId = event.response?.id || null;
        break;

      // ---- Audio ----
      case "response.audio.delta":
        if (event.delta) {
          const audioData = base64ToArrayBuffer(event.delta);
          this.emit("audio", audioData);
        }
        break;

      case "response.audio.done":
        // Audio stream complete for this response item
        break;

      // ---- Text / transcript ----
      case "response.audio_transcript.delta":
        if (event.delta) {
          this.emit("outputTranscription", event.delta);
        }
        break;

      case "response.audio_transcript.done":
        // Full transcript available
        break;

      case "response.text.delta":
        if (event.delta) {
          const content: ServerContent = {
            modelTurn: { parts: [{ text: event.delta }] },
          };
          this.emit("content", content);
        }
        break;

      case "response.text.done":
        break;

      // ---- Input audio transcript (user speech) ----
      case "conversation.item.input_audio_transcription.completed":
        if (event.transcript) {
          this.emit("inputTranscription", event.transcript);
        }
        break;

      // ---- Function calls ----
      case "response.function_call_arguments.delta":
        // Accumulate function call arguments
        if (event.call_id) {
          const existing = this._pendingFunctionCalls.get(event.call_id);
          if (existing) {
            existing.args += event.delta || "";
          } else {
            this._pendingFunctionCalls.set(event.call_id, {
              id: event.call_id,
              name: event.name || "",
              args: event.delta || "",
            });
          }
        }
        break;

      case "response.function_call_arguments.done":
        if (event.call_id) {
          const pending = this._pendingFunctionCalls.get(event.call_id);
          if (pending) {
            // Finalize args
            pending.args = event.arguments || pending.args;
            pending.name = event.name || pending.name;
          }
        }
        break;

      case "response.output_item.done":
        // Check if this is a function call output item
        if (event.item?.type === "function_call") {
          const callId = event.item.call_id;
          const name = event.item.name;
          let args: Record<string, any> = {};

          try {
            args = JSON.parse(event.item.arguments || "{}");
          } catch {
            // Try from pending
            const pending = this._pendingFunctionCalls.get(callId);
            if (pending) {
              try {
                args = JSON.parse(pending.args || "{}");
              } catch {
                args = {};
              }
            }
          }

          // Clean up pending
          this._pendingFunctionCalls.delete(callId);

          // Emit as tool call in our format
          const toolCall: ToolCall = {
            functionCalls: [
              {
                id: callId,
                name: name,
                args: args,
              },
            ],
          };

          logger.tool("Tool call received from Azure:", toolCall);
          this.emit("toolcall", toolCall);
        }
        break;

      // ---- Turn management ----
      case "response.done":
        this._currentResponseId = null;
        // Response complete — check if it completed normally
        if (event.response?.status === "completed") {
          this.emit("turncomplete");
        } else if (event.response?.status === "cancelled") {
          this.emit("interrupted");
        }
        break;

      case "input_audio_buffer.speech_started":
        // User started speaking — interrupt current response
        this.emit("interrupted");
        break;

      case "input_audio_buffer.speech_stopped":
        // User stopped speaking
        break;

      case "input_audio_buffer.committed":
        // Audio buffer committed for processing
        break;

      // ---- Rate limit ----
      case "rate_limits.updated":
        break;

      // ---- Error ----
      case "error": {
        const errorCode = event.error?.code;
        // response_cancel_not_active is benign — it means we called response.cancel
        // when no response was in progress (e.g. cancel arrived just after the turn ended).
        // Treat it as a warning so it doesn't bubble up as a fatal error.
        if (errorCode === 'response_cancel_not_active') {
          logger.warn('[AZURE] response.cancel ignored — no active response (this is normal)');
          break;
        }
        console.error("❌ [AZURE] Server error:", event.error);
        this.emit("error", new ErrorEvent("error", { message: event.error?.message || "Unknown server error" }));
        break;
      }

      default:
        logger.websocket("Unhandled Azure event type:", eventType);
        break;
    }
  }

  public disconnect() {
    if (!this._ws) {
      logger.connection("No active WebSocket to disconnect");
      return false;
    }
    logger.connection("Disconnecting from Azure OpenAI Realtime API");
    if (this._ws.readyState === WebSocket.OPEN) {
      this._ws.close();
    }
    this._ws = null;
    this._status = "disconnected";
    this._pendingFunctionCalls.clear();
    logger.connection("Successfully disconnected");
    return true;
  }

  /**
   * Send realtime audio/image input.
   * Azure Realtime expects raw audio appended to the input_audio_buffer.
   */
  sendRealtimeInput(chunks: Array<{ mimeType: string; data: string }>) {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) {
      logger.warn("Cannot send realtime input - WebSocket not open");
      return;
    }

    for (const ch of chunks) {
      if (ch.mimeType.startsWith("audio/")) {
        // Send audio data to Azure's input audio buffer
        this._ws.send(
          JSON.stringify({
            type: "input_audio_buffer.append",
            audio: ch.data, // base64 PCM16 audio
          })
        );
      } else if (ch.mimeType.startsWith("image/")) {
        // Azure Realtime supports images via conversation items
        // Send as a conversation item with image content
        this._ws.send(
          JSON.stringify({
            type: "conversation.item.create",
            item: {
              type: "message",
              role: "user",
              content: [
                {
                  type: "input_image",
                  image: ch.data, // base64 image data
                },
              ],
            },
          })
        );
        // Trigger a response after sending image
        this._ws.send(JSON.stringify({ type: "response.create" }));
      }
    }
  }

  /**
   * Send tool/function call response back to Azure.
   * @param toolResponse   The function outputs to send.
   * @param silent         When true, skips the trailing response.create so the AI
   *                       does NOT generate a verbal response (used for transitions).
   */
  sendToolResponse(toolResponse: ClientToolResponse, silent: boolean = false) {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) {
      console.warn("⚠️ [AZURE] Cannot send tool response - WebSocket not open");
      return;
    }

    if (toolResponse.functionResponses && toolResponse.functionResponses.length) {
      for (const fr of toolResponse.functionResponses) {
        logger.tool("Sending function call output to Azure:", fr);

        // Send conversation item with function call output
        this._ws.send(
          JSON.stringify({
            type: "conversation.item.create",
            item: {
              type: "function_call_output",
              call_id: fr.id,
              output: typeof fr.response === "string" ? fr.response : JSON.stringify(fr.response),
            },
          })
        );
      }

      // Only trigger a model response when we are NOT about to transition away.
      // For transitions, the old WebSocket will be abandoned so there is no point
      // in generating (and possibly hearing) a response on it.
      if (!silent) {
        this._ws.send(JSON.stringify({ type: "response.create" }));
      }
    }
  }

  /**
   * Cancel any in-progress response generation.
   * Sends response.cancel to the Azure Realtime API which immediately stops
   * audio output and function-call generation for the current turn.
   * Call this before muting the mic to prevent partially-generated audio from playing.
   */
  cancelCurrentResponse() {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return;
    logger.info('[AZURE] Cancelling current response');
    this._ws.send(JSON.stringify({ type: 'response.cancel' }));
  }

  /**
   * Clear the server-side input audio buffer.
   * Removes any buffered user audio that has not yet been committed to a turn,
   * preventing Azure VAD from treating it as speech when the mic is unmuted.
   */
  clearInputAudioBuffer() {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return;
    logger.info('[AZURE] Clearing input audio buffer');
    this._ws.send(JSON.stringify({ type: 'input_audio_buffer.clear' }));
  }

  /**
   * Send text content to the model.
   */
  send(parts: Part | Part[], turnComplete: boolean = true) {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) {
      console.warn("⚠️ [AZURE] Cannot send content - WebSocket not open");
      return;
    }

    const partsArray = Array.isArray(parts) ? parts : [parts];

    // Build content items for Azure
    const contentItems: any[] = [];

    for (const part of partsArray) {
      if (part.text) {
        contentItems.push({
          type: "input_text",
          text: part.text,
        });
      }
      if (part.inlineData) {
        if (part.inlineData.mimeType.startsWith("image/")) {
          contentItems.push({
            type: "input_image",
            image: part.inlineData.data,
          });
        }
      }
    }

    if (contentItems.length > 0) {
      // Create a conversation item
      this._ws.send(
        JSON.stringify({
          type: "conversation.item.create",
          item: {
            type: "message",
            role: "user",
            content: contentItems,
          },
        })
      );

      if (turnComplete) {
        // Trigger response generation
        this._ws.send(JSON.stringify({ type: "response.create" }));
      }
    }
  }
}
